import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import YouTube from 'react-youtube'

import { secsToMinAndSecs } from '../../../utils/helpers'
import LoadingIndicator from '../LoadingIndicator'
import Breakpoint from './Breakpoint/index'
import { ReactComponent as PauseIcon } from './pause.svg'
import { ReactComponent as PlayIcon } from './play.svg'
import styles from './VideoTutorial.module.scss'

const hit = (time, breakpoint) => time < breakpoint && Math.abs(time - breakpoint) < 0.5

export const VideoContext = createContext({
  play: null,
  goToAndPlay: null
})

const VideoTutorialSet = ({ steps }) => {
  const activeStep = 0

  return (
    <>
      <VideoTutorial allSteps={steps} activeStep={activeStep} />
    </>
  )
}

const VideoTutorial = ({ allSteps }) => {
  const [activeStep, setActiveStep] = useState(0)

  const breakpoints = allSteps[activeStep].breakpoints

  const [playerReady, setPlayerReady] = useState(false)
  const [player, setPlayer] = useState()
  const [vState, setVState] = useState()
  const [message, setTutorialMessage] = useState()
  const [isShowingInstruction, setShowingInstruction] = useState()
  const [paused, setPaused] = useState(true)
  const [videoTime, setVideoTime] = useState()

  useEffect(() => {
    if (vState === 3) {
      setPlayerReady(true)
    }
  }, [player, vState])

  function setMessage(message, isIntruction = false) {
    setTutorialMessage(message)
    setShowingInstruction(isIntruction)
  }

  useEffect(() => {
    setVideoTime(0)
    setTutorialMessage(allSteps[activeStep].breakpoints[0].message)
    if (player) {
      player.seekTo(0)
      player.stopVideo()
      setPlayerReady(false)
    }
  }, [activeStep, allSteps, player])

  const vStateRef = useRef(vState)
  vStateRef.current = vState

  const checkTime = useCallback(() => {
    if (vStateRef.current === 1) {
      setPaused(false)
      const time = player.getCurrentTime()
      setVideoTime(time)
      const stopMatch = breakpoints.find(b => hit(time, b.end))
      const messageMatch = breakpoints.find(b => time < b.end && time > b.start)

      if (messageMatch) {
        setMessage(messageMatch.message)
      }

      if (stopMatch) {
        setMessage(stopMatch.instruction, true)
        player.seekTo(stopMatch.end)
        player.pauseVideo()
        setPaused(true)
        return
      }

      window.setTimeout(checkTime, 500)
    }
  }, [breakpoints, player])

  useEffect(() => {
    if (vState === 1) {
      checkTime()
    }
  }, [checkTime, vState])

  const opts = {
    playerVars: {
      autoplay: 0,
      controls: 0,
      showinfo: 0,
      rel: 0,
      fs: 0
    }
  }

  function play() {
    if (player) {
      player.playVideo()
    }
  }

  function goToAndPlay(time) {
    if (player) {
      player.seekTo(time)
      player.playVideo()
    }
  }

  function videoReady(e) {
    setPlayer(e.target)
    setTimeout(() => {
      e.target.pauseVideo()
    }, 1000)
  }

  function pause() {
    if (player && vStateRef.current === 1) {
      player.pauseVideo()
      return
    }

    if (player) {
      player.playVideo()
    }
  }

  return (
    <VideoContext.Provider
      value={{ goToAndPlay, play, setActiveStep, isReady: !!player }}
    >
      <div className={styles.wrapper}>
        {!playerReady && (
          <div className={styles.loading}>
            <LoadingIndicator />
          </div>
        )}
        <div className={playerReady ? styles.container : styles.containerLoading}>
          <div
            className={[styles.videoBox, paused ? styles.videoBoxPaused : ''].join(' ')}
          >
            <div className={styles.videoBoxInner}>
              <YouTube
                videoId={allSteps[activeStep].youtubeId}
                opts={opts}
                onReady={videoReady}
                className={styles.vidIframe}
                onStateChange={state => setVState(state.data)}
              />
            </div>
            <button className={styles.playButton} onClick={pause}>
              {isShowingInstruction ? null : vStateRef.current === 1 ? (
                <PauseIcon />
              ) : (
                <PlayIcon />
              )}
            </button>
          </div>
          <div className={styles.sidebar}>
            <div className={styles.message}>
              {!message && breakpoints.find(b => b.start === 0).message}
              {message}
            </div>
            <div className={styles.breakpoints}>
              {allSteps.map((s, i) => (
                <Step onClick={() => setActiveStep(i)} step={s} isDone={i < activeStep}>
                  {i === activeStep &&
                    s.breakpoints.map(b => (
                      <Breakpoint
                        {...b}
                        seekTo={goToAndPlay}
                        pause={pause}
                        videoTime={videoTime}
                        playing={vStateRef.current === 1}
                      />
                    ))}
                </Step>
              ))}
            </div>
          </div>
        </div>
      </div>
    </VideoContext.Provider>
  )
}

const Step = ({ children, onClick, step }) => {
  const totalDuration = step.breakpoints
    .filter(b => b.end)
    .map(b => b.end - b.start)
    .reduce((prev, current) => prev + current, 0)

  return (
    <div className={styles.step}>
      <div onClick={onClick} className={styles.stepTitle}>
        <div className={styles.stepName}>{step.name}</div>
        <div className={styles.stepDuration}>{secsToMinAndSecs(totalDuration)}</div>
      </div>
      {children && <div className={styles.breakpointsInner}>{children}</div>}
    </div>
  )
}

export default VideoTutorialSet
